import React, { useState, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { MenuItem } from "@mui/material";
import { C, getAuthHeaders, fmtINR } from "./ipd-billing/constants";
import IpdTextField from "./ipd/utils/IpdTextField";
import { IPD_FIELD_COLORS } from "./ipd/utils/ipdFieldStyles";
import IpdDateField from "./ipd/utils/IpdDateField";
import IpdButton from "./ipd/utils/IpdButton";
import SvgIcon from "./ipd-billing/SvgIcon";
import IPDBillingLayout from "./ipd-billing/components/IPDBillingLayout";
import PatientSearchBar from "./ipd-billing/components/PatientSearchBar";
import CoverageEligibilityPanel from "./ipd-billing/components/CoverageEligibilityPanel";
import InsuranceDetailsService from "../services/InsuranceDetailsService";
import { payerTypeLabel } from "./ipd/utils/payerLabels";
import {
  getLedgerAudit,
  getInsuranceApproval,
  setInsuranceApproval,
  resolveAdmissionId,
  persistAdmissionContext,
  restoreAdmissionContext,
  hydrateRestoredPatient,
  clearAdmissionContext,
  listAllocatedAdmissions,
  admissionToPatientContext,
} from "./ipd-billing/billing-api";

// ── HELPERS ─────────────────────────────────────────────────────────────────

// Read the first non-empty value among several possible key names (snake/camel).
const pick = (obj, keys, fallback = "") => {
  for (const k of keys) {
    const v = obj?.[k];
    if (v !== undefined && v !== null && v !== "") return v;
  }
  return fallback;
};

// Parse an amount that may arrive as a string ("6140.00") or number. null/blank → null.
const toAmount = (v) => {
  if (v === null || v === undefined || v === "") return null;
  const n = parseFloat(v);
  return Number.isFinite(n) ? n : null;
};

// Classify a fetch failure:
//  - "no-record":     JSON 404 — the route exists, this admission just has no row yet (benign).
//  - "not-deployed":  non-JSON 404 (Django HTML error page) — the URL matched no backend route,
//                     i.e. the endpoint isn't deployed where we're calling. Must surface loudly.
//  - "hard":          network failure / 5xx / other 4xx.
const classifyFetchError = (err) => {
  const res = err?.response;
  if (!res) return "hard";
  if (res.status === 404) {
    const contentType = String(res.headers?.["content-type"] || "");
    return contentType.includes("application/json") ? "no-record" : "not-deployed";
  }
  return "hard";
};

// Persisted eligibility / pre-auth capture (the only fields the backend insurance_details
// model stores). pre_auth_* doubles as the Stage-2 "authorization" date/number.
const EMPTY_FORM = {
  member_card_number: "",
  uhid: "",
  eligibility_check: "No",
  eligibility_date: "",
  eligibility_reference_number: "",
  pre_auth_date: "",
  pre_auth_number: "",
};

// ── SHARED STYLES ─────────────────────────────────────────────────────────────
const cardStyle = {
  background: C.card,
  borderRadius: "12px",
  border: `1px solid ${C.border}`,
  boxShadow: "0 2px 8px rgba(13,35,63,0.07)",
};
const labelStyle = {
  fontSize: "11px",
  fontWeight: "700",
  color: C.textSub,
  textTransform: "uppercase",
  letterSpacing: "0.06em",
  display: "block",
  // Pinned explicitly: without it the value is inherited, so the same style object
  // rendered on different elements resolved to different heights and the read-only
  // labels sat lower than the editable ones.
  lineHeight: 1.4,
  marginBottom: "6px",
};
const sectionLabelStyle = {
  fontSize: "12px",
  fontWeight: "800",
  color: C.text,
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  display: "flex",
  alignItems: "center",
  gap: "8px",
  marginBottom: "14px",
};
// auto-fill, not auto-fit: auto-fit collapses the tracks a partly filled row doesn't
// use, so the remaining fields stretch to absorb the space — which made the lone
// Member / Card ID field span the whole row. auto-fill keeps every track, so a field
// is one column wide no matter how many siblings it has.
const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))",
  gap: "16px",
};

// ── SMALL PRESENTATIONAL COMPONENTS ────────────────────────────────────────────
// Read-only counterpart to Field — a disabled IpdTextField so it shares the exact
// geometry (height, radius, border, font) of the editable fields beside it, with the
// disabled grey background as the read-only cue. Long values clip, hence the title.
function DisplayField({ label, value }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>{label}</label>
      <IpdTextField
        fullWidth
        disabled
        value={value || "—"}
        title={value || ""}
        sx={{
          // MUI's disabled defaults drop the field shadow and wash the border out to
          // action.disabled, which made these read as a different control than the
          // editable fields beside them. Restore both — the grey fill is the only cue.
          "& .MuiOutlinedInput-root.Mui-disabled": {
            boxShadow: IPD_FIELD_COLORS.shadow,
            "& fieldset": { borderColor: IPD_FIELD_COLORS.border },
          },
          "& .MuiOutlinedInput-input.Mui-disabled": {
            WebkitTextFillColor: value ? C.text : C.textSub,
            fontWeight: 600,
          },
        }}
      />
    </div>
  );
}

// Labelled editable field wrapper (sizing handled by the parent grid).
function Field({ label, required, children }) {
  return (
    <div style={{ minWidth: 0 }}>
      <label style={labelStyle}>
        {label}
        {required ? " *" : ""}
      </label>
      {children}
    </div>
  );
}

function StageBadge({ done }) {
  return (
    <span
      style={{
        fontSize: "10px",
        fontWeight: "700",
        padding: "3px 9px",
        borderRadius: "20px",
        background: done ? "#e9f7ef" : "#f1f3f5",
        color: done ? C.success : C.textSub,
        border: `1px solid ${done ? "#b7e4c7" : C.border}`,
        whiteSpace: "nowrap",
      }}
    >
      {done ? "Done" : "Pending"}
    </span>
  );
}

// Segmented Manual / Auto switch sitting to the right of the page title.
function ModeTab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        border: "none",
        cursor: "pointer",
        fontSize: "12px",
        fontWeight: "700",
        padding: "6px 18px",
        borderRadius: "8px",
        background: active ? "#fff" : "transparent",
        color: active ? C.primary : C.textSub,
        boxShadow: active ? "0 1px 3px rgba(13,35,63,0.14)" : "none",
      }}
    >
      {children}
    </button>
  );
}

function StageCard({ n, title, icon, done, badge, children }) {
  return (
    <div
      style={{
        border: `1px solid ${C.border}`,
        borderRadius: "10px",
        padding: "16px",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: "14px",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: "9px" }}>
          <div
            style={{
              width: "28px",
              height: "28px",
              borderRadius: "8px",
              background: done ? "#e9f7ef" : C.accent,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <SvgIcon name={icon} size={15} color={done ? C.success : C.primary} />
          </div>
          <div>
            <div style={{ fontSize: "10px", fontWeight: "700", color: C.textSub, textTransform: "uppercase", letterSpacing: "0.06em" }}>
              Stage {n}
            </div>
            <div style={{ fontSize: "13px", fontWeight: "700", color: C.text }}>{title}</div>
          </div>
        </div>
        {badge || <StageBadge done={done} />}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>{children}</div>
    </div>
  );
}

// ── PAGE ────────────────────────────────────────────────────────────────────
export default function IPDBillingInsurance() {
  const location = useLocation();
  const passedPatient = location.state?.patient || null;

  // Patient selection uses the shared billing PatientSearchBar / admission context,
  // same as the other IPD billing screens.
  const [activeAdmissionId, setActiveAdmissionId] = useState(() => resolveAdmissionId(passedPatient));
  const [selectedPatient, setSelectedPatient] = useState(() => {
    if (passedPatient?.admission_id || passedPatient?.admissionId) return passedPatient;
    return restoreAdmissionContext();
  });
  const [admissions, setAdmissions] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loadingAdmissions, setLoadingAdmissions] = useState(false);
  const [searchError, setSearchError] = useState("");
  // Bumped on every explicit patient selection (and after a save) so the fetch effect
  // re-fires even when the resolved ids are unchanged — every click visibly re-fetches.
  const [selectionVersion, setSelectionVersion] = useState(0);
  const [admissionDetails, setAdmissionDetails] = useState(null); // API 3 — registration insurance (read-only)
  const [ledger, setLedger] = useState(null); // ledger-audit → Total Bill Payable (read-only)
  const [isLoadingDetails, setIsLoadingDetails] = useState(false);

  // Capture mode: "manual" is the existing TPA capture workflow; "auto" is not built yet.
  const [mode, setMode] = useState("manual");

  const [form, setForm] = useState(EMPTY_FORM);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  // Stage 3 co-pay: the insurer-approved (LOA) amount lives on the billing account.
  const [approval, setApproval] = useState(null);       // {total_bill, insurance_approved_amount, patient_payable, ...}
  const [approvedInput, setApprovedInput] = useState("");
  const [applyingApproval, setApplyingApproval] = useState(false);

  // The insurance APIs are keyed by patient_id + admission_id; an id-only restored
  // context has no patient_id until the admissions list hydrates it below.
  const patientId = pick(selectedPatient || {}, ["patient_id", "patientId", "pat_id"]);
  const admissionId = activeAdmissionId;
  const hasPatient = Boolean(patientId && admissionId);

  const loadAdmissions = useCallback(async () => {
    setLoadingAdmissions(true);
    setSearchError("");
    try {
      setAdmissions(await listAllocatedAdmissions("Allocated"));
    } catch (err) {
      setSearchError(err.message || "Failed to load patients");
      setAdmissions([]);
    } finally {
      setLoadingAdmissions(false);
    }
  }, []);

  useEffect(() => { loadAdmissions(); }, [loadAdmissions]);

  // Fill in an id-only restore (name/bed/doctor/payer) once the list lands.
  useEffect(() => {
    const hydrated = hydrateRestoredPatient(selectedPatient, admissions);
    if (hydrated !== selectedPatient) {
      persistAdmissionContext(hydrated);
      setSelectedPatient(hydrated);
    }
  }, [admissions, selectedPatient]);

  useEffect(() => {
    if (passedPatient) {
      const id = resolveAdmissionId(passedPatient);
      if (id) {
        persistAdmissionContext(passedPatient);
        setActiveAdmissionId(id);
        setSelectedPatient(passedPatient);
      }
    }
  }, [passedPatient]);

  const handleSelectAdmission = useCallback((row) => {
    const patient = admissionToPatientContext(row);
    persistAdmissionContext(patient);
    setSelectedPatient(patient);
    setActiveAdmissionId(String(row.admission_id));
    setSearchQuery("");
    setError("");
    setSelectionVersion((v) => v + 1);
  }, []);

  const handleClearPatient = useCallback(() => {
    setSelectedPatient(null);
    setActiveAdmissionId("");
    clearAdmissionContext();
    setSearchQuery("");
    setError("");
  }, []);

  // Fetch everything for the selected admission in parallel:
  //   API 2  insurance_details            → eligibility / pre-auth capture form
  //   API 3  insurance_admission_details  → read-only payer / policy info
  //   ledger ipd_admission/{id}/billing/ledger-audit → Total Bill Payable (net_outstanding)
  // allSettled so one source failing doesn't blank the others; a JSON 404 / no-record on
  // the insurance sources is treated as "empty, not yet captured" rather than a hard error.
  useEffect(() => {
    if (!patientId || !admissionId) {
      setAdmissionDetails(null);
      setLedger(null);
      setForm(EMPTY_FORM);
      setApproval(null);
      setApprovedInput("");
      return;
    }
    let cancelled = false;

    const fetchAll = async () => {
      setIsLoadingDetails(true);
      setError("");

      const [detailsRes, admissionRes, ledgerRes, approvalRes] = await Promise.allSettled([
        InsuranceDetailsService.getInsuranceDetails({ patientId, admissionId }, getAuthHeaders()),
        InsuranceDetailsService.getInsuranceAdmissionDetails({ patientId, admissionId }, getAuthHeaders()),
        getLedgerAudit(admissionId),
        getInsuranceApproval(admissionId),
      ]);
      if (cancelled) return;

      const failures = [];

      // ── API 2 → eligibility / pre-auth capture form ──
      if (detailsRes.status === "fulfilled") {
        const data = detailsRes.value?.data || {};
        setForm({
          member_card_number: pick(data, ["member_card_number", "memberCardNumber"]),
          uhid: pick(data, ["uhid", "UHID"]) || selectedPatient?.uhid || "",
          // Backend sends a boolean; the form uses a Yes/No <select>.
          eligibility_check: data.eligibility_check === true ? "Yes" : "No",
          eligibility_date: pick(data, ["eligibility_date", "eligibilityDate"]),
          eligibility_reference_number: pick(data, ["eligibility_reference_number", "eligibilityReferenceNumber"]),
          pre_auth_date: pick(data, ["pre_auth_date", "preAuthDate"]),
          pre_auth_number: pick(data, ["pre_auth_number", "preAuthNumber"]),
        });
      } else {
        setForm({ ...EMPTY_FORM, uhid: selectedPatient?.uhid || "" });
        failures.push(classifyFetchError(detailsRes.reason));
      }

      // ── API 3 → registration insurance (payer / policy) ──
      if (admissionRes.status === "fulfilled") {
        setAdmissionDetails(admissionRes.value?.data || {});
      } else {
        setAdmissionDetails({});
        failures.push(classifyFetchError(admissionRes.reason));
      }

      // ── Ledger → Total Bill Payable (soft-fail: just show "—") ──
      if (ledgerRes.status === "fulfilled") {
        setLedger(ledgerRes.value || null);
      } else {
        setLedger(null);
        console.warn("Failed to load billing ledger:", ledgerRes.reason);
      }

      // ── Insurance approval (co-pay) → total bill / approved / patient payable ──
      if (approvalRes.status === "fulfilled" && approvalRes.value) {
        setApproval(approvalRes.value);
        const amt = approvalRes.value.insurance_approved_amount;
        setApprovedInput(amt != null && Number(amt) > 0 ? String(amt) : "");
      } else {
        setApproval(null);
        setApprovedInput("");
        if (approvalRes.status === "rejected") console.warn("Failed to load insurance approval:", approvalRes.reason);
      }

      // "no-record" is benign (patient simply has no insurance row yet) — stay silent.
      if (failures.includes("not-deployed")) {
        toast.error(
          "Insurance API endpoint not found on the server — verify backend deployment of insurance_details/ and insurance_admission_details/."
        );
      } else if (failures.includes("hard")) {
        toast.error("Failed to load insurance details.");
      }

      setIsLoadingDetails(false);
    };

    fetchAll();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [patientId, admissionId, selectionVersion]);

  const setField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    if (error) setError("");
  };

  const handleSave = async () => {
    if (!patientId || !admissionId) {
      setError("Select a patient first.");
      return;
    }
    if (!form.member_card_number.trim()) {
      setError("Member / Card Number is required.");
      return;
    }
    if (!form.uhid.trim()) {
      setError("UHID is required.");
      return;
    }
    if (form.eligibility_check === "Yes" && !form.eligibility_date) {
      setError("Eligibility Date is required when eligibility is confirmed.");
      return;
    }

    setIsSaving(true);
    setError("");
    try {
      const payload = {
        patient_id: patientId,
        admission_id: admissionId,
        member_card_number: form.member_card_number.trim(),
        uhid: form.uhid.trim(),
        // Backend expects a boolean; empty dates must go as null, not "" (DRF DateField).
        eligibility_check: form.eligibility_check === "Yes",
        eligibility_date:
          form.eligibility_check === "Yes" && form.eligibility_date ? form.eligibility_date : null,
        eligibility_reference_number: form.eligibility_reference_number.trim(),
        pre_auth_date: form.pre_auth_date || null,
        pre_auth_number: form.pre_auth_number.trim(),
      };
      const res = await InsuranceDetailsService.saveInsuranceDetails(payload, getAuthHeaders());
      toast.success(res?.message || "Insurance eligibility saved.");
      // Round-trip: re-fetch so the form reflects exactly what the server persisted.
      setSelectionVersion((v) => v + 1);
    } catch (err) {
      console.error("Insurance save error:", err);
      const msg = err?.response?.data?.message || err?.message || "Failed to save insurance details.";
      setError(msg);
      toast.error(msg);
    } finally {
      setIsSaving(false);
    }
  };

  // NHCX results live only in the eligibility service's memory, so a completed Auto
  // check is carried into the Manual capture form (Stage 1) where the existing save
  // path persists it. Deliberately not auto-saved — the user reviews, then saves.
  const handleCopyToManual = useCallback((correlationId) => {
    const now = new Date();
    const iso = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")}`;
    setForm((prev) => ({
      ...prev,
      eligibility_check: "Yes",
      eligibility_date: iso,
      eligibility_reference_number: correlationId || prev.eligibility_reference_number,
    }));
    setMode("manual");
    toast.info("Eligibility copied to the Manual tab — review and click Save.");
  }, []);


  const handleApplyApproval = async () => {
    if (!admissionId) { setError("Select a patient first."); return; }
    const amt = Number(approvedInput || 0);
    if (!Number.isFinite(amt) || amt < 0) { setError("Enter a valid approved amount."); return; }
    setApplyingApproval(true);
    setError("");
    try {
      const res = await setInsuranceApproval(admissionId, { insurance_approved_amount: amt.toFixed(2) });
      setApproval(res || null);
      setApprovedInput(res?.insurance_approved_amount != null ? String(res.insurance_approved_amount) : amt.toFixed(2));
      toast.success("Approved amount applied — patient payable updated.");
    } catch (err) {
      const raw = err?.response?.data?.message || err?.message || "";
      const msg = /negative/i.test(raw)
        ? "Approved amount cannot exceed the total payable."
        : (raw || "Failed to apply approved amount.");
      setError(msg);
      toast.error(msg);
    } finally {
      setApplyingApproval(false);
    }
  };

  // ── Registration insurance (API 3) → payer / policy info ──
  const a = admissionDetails || {};
  const display = {
    provider: pick(a, ["insurance_provider", "insurance_provider_name", "insuranceProviderName"]),
    policy: pick(a, ["insurance_policy_number", "insurance_policy_no", "insurancePolicyNo"]),
    tpa: pick(a, ["tpa_name", "tpaName"]),
    payerType: pick(a, ["payer_type", "payerType"]),
    governmentScheme: pick(a, ["government_scheme", "governmentScheme"]),
    corporateName: pick(a, ["corporate_name", "corporateName"]),
    corporateReference: pick(a, ["corporate_reference", "corporateReference"]),
  };
  const hasRegistrationInsurance = display.provider || display.policy || display.tpa || display.payerType;

  // The cashless workflow only applies to insurance / TPA payers. Registration sends
  // either vocabulary (INSURANCE/TPA from the wizard's financial_class, insurance from
  // the flat payer_type column) — see ipd/utils/payerLabels. A blank payer type means
  // registration captured nothing, so the capture form stays available rather than
  // locking the user out.
  const payerTypeKey = String(display.payerType || "").trim().toLowerCase();
  const isInsurancePayer = !payerTypeKey || payerTypeKey === "insurance" || payerTypeKey === "tpa";


  // ── Ledger (read-only) → Total Bill Payable + supporting figures ──
  const ledgerCards = ledger?.cards || {};
  const netOutstanding = toAmount(ledgerCards.net_outstanding);
  const totalCharges = toAmount(ledgerCards.debits);
  const totalPayments = toAmount(ledgerCards.credits);

  // ── Stage 3 co-pay figures ──
  // Total bill from the billing account (net), approved amount live from the input,
  // patient payable = total − approved (co-pay). Falls back to ledger net outstanding.
  const totalBill = toAmount(approval?.total_bill) ?? netOutstanding;
  const approvedAmount = toAmount(approvedInput) || 0;
  const patientPayableLive =
    totalBill != null ? Math.max(0, totalBill - approvedAmount) : null;

  // ── Stage completion (drives the Done/Pending badges + the X/2 header counter) ──
  const stage1Done = form.eligibility_check === "Yes" && !!form.eligibility_date;
  const stage2Done = !!form.pre_auth_number.trim() && !!form.pre_auth_date;
  const capturedCount = [stage1Done, stage2Done].filter(Boolean).length;

  const busy = isSaving || isLoadingDetails;

  return (
    <IPDBillingLayout activeNav="insurance">
      <div style={{ padding: "28px", display: "flex", flexDirection: "column", gap: "20px" }}>

        {/* ── PATIENT SELECTOR ── */}
        <PatientSearchBar
          admissions={admissions}
          loadingAdmissions={loadingAdmissions}
          searchQuery={searchQuery}
          onSearchChange={setSearchQuery}
          onSelect={handleSelectAdmission}
          selectedPatient={selectedPatient}
          onClear={handleClearPatient}
          onRefresh={loadAdmissions}
          searchError={searchError}
          selectHint="select one to capture insurance & claims"
        />

        {/* ── PAGE TITLE + MODE TABS ── */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "16px", flexWrap: "wrap" }}>
          <div>
            <h2 style={{ margin: "0 0 4px", fontSize: "22px", fontWeight: "800", color: C.text }}>Insurance &amp; TPA Claims</h2>
            <p style={{ margin: 0, fontSize: "13px", color: C.textSub }}>
              Cashless lifecycle · Eligibility → Pre-auth → Settlement
            </p>
          </div>
          <div style={{ display: "flex", gap: "3px", padding: "3px", background: "#f1f3f5", border: `1px solid ${C.border}`, borderRadius: "10px" }}>
            <ModeTab active={mode === "manual"} onClick={() => setMode("manual")}>Manual</ModeTab>
            <ModeTab active={mode === "auto"} onClick={() => setMode("auto")}>Auto</ModeTab>
          </div>
        </div>

        {!hasPatient ? (
          <div style={{ ...cardStyle, padding: "48px", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", borderRadius: "12px", background: C.accent, marginBottom: "12px" }}>
              <SvgIcon name="shield" size={24} color={C.primary} />
            </div>
            <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "4px" }}>No patient selected</div>
            <div style={{ fontSize: "13px", color: C.textSub }}>Use the search above to pick an admitted patient.</div>
          </div>
        ) : mode === "auto" ? (
          // Keyed on the admission: every bit of the panel's state (correlation id,
          // result, overrides, ward) is per-patient, so switching patients has to
          // start it fresh rather than leave the previous answer on screen.
          <CoverageEligibilityPanel
            key={admissionId}
            admissionId={admissionId}
            patient={selectedPatient}
            display={display}
            memberCardNumber={form.member_card_number}
            onCopyToManual={handleCopyToManual}
          />
        ) : !isInsurancePayer ? (
          <div style={{ ...cardStyle, padding: "48px", textAlign: "center" }}>
            <div style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", width: "48px", height: "48px", borderRadius: "12px", background: C.accent, marginBottom: "12px" }}>
              <SvgIcon name="shield" size={24} color={C.primary} />
            </div>
            <div style={{ fontSize: "15px", fontWeight: "700", color: C.text, marginBottom: "4px" }}>Not an insurance / TPA patient</div>
            <div style={{ fontSize: "13px", color: C.textSub }}>
              This admission's payer type is {payerTypeLabel(display.payerType)} — the cashless
              insurance workflow does not apply.
            </div>
          </div>
        ) : (
          <div style={cardStyle}>
            {/* ── CARD HEADER ── */}
            <div style={{ padding: "16px 20px", borderBottom: `1px solid ${C.border}`, display: "flex", alignItems: "center", justifyContent: "space-between", gap: "12px", flexWrap: "wrap" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <SvgIcon name="shield" size={16} color={C.primary} />
                <h3 style={{ margin: 0, fontSize: "15px", fontWeight: "700", color: C.text }}>TPA Manual Capture · Insurance Approval Workflow</h3>
              </div>
              <span style={{ fontSize: "11px", fontWeight: "700", color: C.primary, background: C.accent, border: `1px solid #bfe3fb`, padding: "4px 10px", borderRadius: "20px" }}>
                {capturedCount}/2 captured
              </span>
            </div>

            <div style={{ padding: "20px", display: "flex", flexDirection: "column", gap: "24px", opacity: isLoadingDetails ? 0.55 : 1, pointerEvents: isLoadingDetails ? "none" : "auto" }}>

              {/* ── PAYER / POLICY INFORMATION ── */}
              <div>
                <div style={sectionLabelStyle}>
                  <SvgIcon name="building" size={14} color={C.textSub} /> Payer / Policy Information
                </div>
                {!isLoadingDetails && !hasRegistrationInsurance && (
                  <div style={{ fontSize: "12px", color: C.textSub, marginBottom: "12px" }}>
                    No insurance was captured at registration — payer fields below are read-only and may be empty.
                  </div>
                )}
                {/* Read-only registration data. */}
                <div style={gridStyle}>
                  {display.payerType && <DisplayField label="Payer Type" value={display.payerType} />}
                  <DisplayField label="Insurance / Payer Name" value={display.provider} />
                  <DisplayField label="Policy No." value={display.policy} />
                  <DisplayField label="Patient Name" value={selectedPatient.name} />
                  <DisplayField label="UHID" value={form.uhid} />
                  {display.tpa && <DisplayField label="TPA Name" value={display.tpa} />}
                  {display.governmentScheme && <DisplayField label="Government Scheme" value={display.governmentScheme} />}
                  {display.corporateName && <DisplayField label="Corporate Name" value={display.corporateName} />}
                  {display.corporateReference && <DisplayField label="Corporate Reference" value={display.corporateReference} />}
                </div>

                {/* Its own grid so the one editable field always starts a fresh row,
                    whatever the conditional read-only fields above happen to fill. */}
                <div style={{ ...gridStyle, marginTop: "16px" }}>
                  <Field label="Member / Card ID" required>
                    <IpdTextField fullWidth value={form.member_card_number} onChange={(e) => setField("member_card_number", e.target.value)} placeholder="Member or card number" />
                  </Field>
                </div>
              </div>

              {/* ── THE THREE STAGES ── */}
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(290px, 1fr))", gap: "16px", alignItems: "start" }}>

                {/* Stage 1 — Insurance Eligibility (insurance_details) */}
                <StageCard n={1} title="Insurance Eligibility" icon="shield" done={stage1Done}>
                  <Field label="Eligibility Confirmed">
                    <IpdTextField select fullWidth value={form.eligibility_check} onChange={(e) => setField("eligibility_check", e.target.value)}>
                      <MenuItem value="No">No — Not verified</MenuItem>
                      <MenuItem value="Yes">Yes — Eligible</MenuItem>
                    </IpdTextField>
                  </Field>
                  <Field label="Eligibility Reference">
                    <IpdTextField fullWidth value={form.eligibility_reference_number} onChange={(e) => setField("eligibility_reference_number", e.target.value)} placeholder="ELIG-…" />
                  </Field>
                  <Field label="Eligibility Date">
                    <IpdDateField fullWidth value={form.eligibility_date} onChange={(iso) => setField("eligibility_date", iso)} />
                  </Field>
                </StageCard>

                {/* Stage 2 — Preauthorization (insurance_details). The save action sits in
                    this column, directly under the last of the two stages it persists. */}
                <div style={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                  <StageCard n={2} title="Preauthorization" icon="credit-card" done={stage2Done}>
                    <Field label="Authorization Date">
                      <IpdDateField fullWidth value={form.pre_auth_date} onChange={(iso) => setField("pre_auth_date", iso)} />
                    </Field>
                    <Field label="Authorization No.">
                      <IpdTextField fullWidth value={form.pre_auth_number} onChange={(e) => setField("pre_auth_number", e.target.value)} placeholder="AUTH-…" />
                    </Field>
                  </StageCard>

                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <IpdButton
                      onClick={handleSave}
                      disabled={busy}
                      loading={isSaving}
                      startIcon={<SvgIcon name="check" size={15} color="#fff" />}
                    >
                      {isSaving ? "Saving…" : "Save Eligibility & Pre-Auth"}
                    </IpdButton>
                  </div>
                </div>

                {/* Stage 3 — Final Settlement (read-only, from billing ledger) */}
                <StageCard
                  n={3}
                  title="Final Settlement"
                  icon="bill"
                  done={approvedAmount > 0}
                  badge={
                    <span style={{ fontSize: "10px", fontWeight: "700", padding: "3px 9px", borderRadius: "20px", background: "#f1f3f5", color: C.textSub, border: `1px solid ${C.border}`, whiteSpace: "nowrap" }}>
                      Co-pay
                    </span>
                  }
                >
                  <div>
                    <div style={labelStyle}>Total Bill</div>
                    <div style={{ fontSize: "22px", fontWeight: "800", color: totalBill != null ? C.text : C.textSub }}>
                      {fmtINR(totalBill)}
                    </div>
                  </div>

                  {/* Insurer-approved (LOA) amount — the billing user enters what the TPA sanctioned. */}
                  <Field label="Insurance Approved Amount">
                    <div style={{ display: "flex", gap: "8px" }}>
                      <IpdTextField
                        type="number"
                        inputProps={{ min: 0, step: 0.01 }}
                        value={approvedInput}
                        onChange={(e) => { setApprovedInput(e.target.value); if (error) setError(""); }}
                        placeholder="0.00"
                        sx={{ flex: 1 }}
                      />
                      <IpdButton
                        onClick={handleApplyApproval}
                        disabled={busy}
                        loading={applyingApproval}
                      >
                        Apply
                      </IpdButton>
                    </div>
                  </Field>

                  {/* Patient pays only their share; the insurer portion stays as outstanding to the TPA. */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", background: C.accent, border: `1px solid #bfe3fb`, borderRadius: "8px", padding: "10px 12px" }}>
                    <span style={{ fontSize: "12px", fontWeight: "700", color: C.primary }}>Patient Payable</span>
                    <span style={{ fontSize: "18px", fontWeight: "800", color: C.primary }}>{fmtINR(patientPayableLive)}</span>
                  </div>

                  <div style={{ display: "flex", flexDirection: "column", gap: "8px", borderTop: `1px solid ${C.border}`, paddingTop: "12px" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                      <span style={{ color: C.textSub }}>Insurer Approved</span>
                      <span style={{ fontWeight: "700", color: C.text }}>{fmtINR(approvedAmount)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                      <span style={{ color: C.textSub }}>Total Charges</span>
                      <span style={{ fontWeight: "700", color: C.text }}>{fmtINR(totalCharges)}</span>
                    </div>
                    <div style={{ display: "flex", justifyContent: "space-between", fontSize: "12px" }}>
                      <span style={{ color: C.textSub }}>Payments Received</span>
                      <span style={{ fontWeight: "700", color: C.success }}>{fmtINR(totalPayments)}</span>
                    </div>
                  </div>
                </StageCard>
              </div>

              {error && (
                <div style={{ fontSize: "13px", color: C.danger, fontWeight: "600" }}>{error}</div>
              )}
            </div>
          </div>
        )}
      </div>
    </IPDBillingLayout>
  );
}
